/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import AsyncSelect from "react-select/async";
import { useTranslation } from "react-i18next";

import { getData, postData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";
import { getCustomStyles } from "Config/Config";
import { getMemberOptions } from "helper/ContactHelper/ContactHelper";
import { assetImages } from "constants";
import PatientDetailsCard from "../PatientDetailsCard/PatientDetailsCard";

const SaveBloodPressurePopup = ({
  patientObj = null,
  afterActionClose = () => {},
  selectedBloodPressureId = null, // Default value for selectedBloodPressureId
  setSelectedBloodPressureId = () => {},
}) => {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");
  const currentUser = JSON.parse(localStorage.getItem("userInfo"));

  const userObj = {
    label: `${currentUser.name ?? currentUser.email} ${
      currentUser.surname ?? ""
    }`,
    value: currentUser._id,
  };

  // States for input fields
  const [systolic, setSystolic] = useState("");
  const [diastolic, setDiastolic] = useState("");
  const [assignTo, setAssignTo] = useState(null);

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] =
    useState(false); /* New state for loading blood pressure details */
  const [errors, setErrors] = useState({});
  const offcanvasBodyRef = useRef(null);

  const validateForm = () => {
    const newErrors = {};
    if (!systolic) {
      newErrors.systolic = t("Please enter systolic value!");
    } else if (isNaN(systolic) || systolic <= 0) {
      newErrors.systolic = t("Please enter a valid systolic number!");
    }
    if (!diastolic) {
      newErrors.diastolic = t("Please enter diastolic value!");
    } else if (isNaN(diastolic) || diastolic <= 0) {
      newErrors.diastolic = t("Please enter a valid diastolic number!");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Async load options for Assign To (from AddAllergyModal)
  const loadAssineeOptions = async (inputValue) => {
    try {
      const requestUrl =
        url.API_BASE_URL +
        url.API_GET_USER_LIST_BY_USER +
        `?token=${token}&userstringinput=${inputValue}&modulefrom=backend&filterroleslug=PATIENT`;

      const response = await getData(requestUrl);
      return response.data;
    } catch (error) {
      return [];
    }
  };

  //function for get blood pressure details
  const getBloodPressureDetails = async () => {
    try {
      setIsLoading(true); /* Set loading to true before API call */
      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_BLOOD_PRESSURE_BY_ID +
        `/${selectedBloodPressureId}?token=${token}`;

      const response = await getData(requestUrl);

      if (response.status) {
        const bloodPressureData = response.data;

        setSystolic(bloodPressureData.systolic);
        setDiastolic(bloodPressureData.diastolic);
        setAssignTo(bloodPressureData?.uservalue);
      }
    } catch (error) {
      console.log(error.message);
    } finally {
      setIsLoading(
        false
      ); /* Set loading to false after API call (success or failure) */
    }
  };

  const saveBloodPressureHandler = async () => {
    if (!validateForm()) {
      if (offcanvasBodyRef.current) {
        offcanvasBodyRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }

    try {
      setIsSaving(true);
      const apiData = {
        systolic: parseInt(systolic),
        diastolic: parseInt(diastolic),
        user: assignTo ? assignTo?.value : null,
      };

      // Assuming an API_CREATE_BLOOD_PRESSURE endpoint exists in UrlHelper.js
      let requestUrl = url.API_BASE_URL;
      let response = {};

      if (selectedBloodPressureId) {
        requestUrl +=
          url.API_UPDATE_BLOOD_PRESSURE +
          `/${selectedBloodPressureId}?token=${token}`;
        response = await putData(requestUrl, apiData);
      } else {
        requestUrl += url.API_CREATE_BLOOD_PRESSURE + `?token=${token}`;
        response = await postData(requestUrl, apiData);
      }

      setIsSaving(false);

      if (response.status) {
        resetHandler();
        window.bootstrap.Offcanvas.getInstance(
          document.querySelector("#saveBloodPressureOffCanvas")
        ).hide();
        afterActionClose([response.data]);
      }
    } catch (error) {
      console.error("Error saving blood pressure:", error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const resetHandler = () => {
    setSystolic("");
    setDiastolic("");
    setAssignTo(patientObj);
    setErrors({});
    setSelectedBloodPressureId(null);
  };

  useEffect(() => {
    if (patientObj && !selectedBloodPressureId) {
      setAssignTo(patientObj);
    }
  }, [patientObj, selectedBloodPressureId]);

  useEffect(() => {
    if (selectedBloodPressureId) {
      getBloodPressureDetails();
    }
  }, [selectedBloodPressureId]);

  return (
    <div
      className="offcanvas lg offcanvas-end bg-white border-0 shadow-lg"
      tabIndex="-1"
      id="saveBloodPressureOffCanvas"
      aria-labelledby="saveBloodPressureOffCanvas"
    >
      <div className="offcanvas-header bg-primary text-white p-4 pe-5 pb-3 border-bottom border-gray-300">
        <div className="d-flex align-items-center gap-3">
          <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-symbols-outlined text-primary fs-5 icon-md">
              eco
            </span>
          </div>
          <div>
            <h3
              className="offcanvas-title fw-bold text-white mb-0"
              id="saveBloodPressureOffCanvas"
            >
              {selectedBloodPressureId
                ? t("Edit Blood Pressure")
                : t("Save Blood Pressure")}
            </h3>
            <p className="text-white fs-sm mb-0">
              {selectedBloodPressureId
                ? t("Manage existing blood pressure details")
                : t("Enter and save blood pressure readings")}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close p-1 bg-gray-200 rounded-circle shadow-sm border-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={resetHandler}
        ></button>
      </div>
      <div className="offcanvas-body p-4" ref={offcanvasBodyRef}>
        {isLoading ? (
          <div className="d-flex flex-column justify-content-center align-items-center position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-75 z-index-1050">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">
                {t("Loading blood pressure details...")}
              </span>
            </div>
            <p className="text-primary mt-3 fw-bold">
              {t("Loading blood pressure details...")}
            </p>
          </div>
        ) : null}

        {!isLoading && (
          <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm bg-light">
            <form
              onSubmit={(e) => e.preventDefault()}
              className="d-flex flex-column h-100"
            >
              <div className="fields-container flex-fill">
                {/* Systolic and Diastolic on the same line */}
                <div className="row">
                  <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
                    <div className="form-group mb-3 mb-sm-4 p-3 border rounded-8 shadow-sm bg-white">
                      <label
                        htmlFor="systolic"
                        className="d-block fs-sm fw-semibold mb-2 text-dark"
                      >
                        Systolic (mmHg) *
                      </label>
                      <input
                        type="number"
                        id="systolic"
                        className={`form-control fs-sm shadow-none ${
                          errors.systolic
                            ? "is-invalid"
                            : "border border-gray-300"
                        }`}
                        placeholder="e.g., 120"
                        value={systolic}
                        onChange={(e) => {
                          setSystolic(e.target.value);
                          if (errors.systolic) {
                            setErrors((prev) => ({ ...prev, systolic: "" }));
                          }
                        }}
                      />
                      {errors.systolic && (
                        <div className="invalid-feedback">
                          {errors.systolic}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
                    <div className="form-group mb-3 mb-sm-4 p-3 border rounded-8 shadow-sm bg-white">
                      <label
                        htmlFor="diastolic"
                        className="d-block fs-sm fw-semibold mb-2 text-dark"
                      >
                        Diastolic (mmHg) *
                      </label>
                      <input
                        type="number"
                        id="diastolic"
                        className={`form-control fs-sm shadow-none ${
                          errors.diastolic
                            ? "is-invalid"
                            : "border border-gray-300"
                        }`}
                        placeholder="e.g., 80"
                        value={diastolic}
                        onChange={(e) => {
                          setDiastolic(e.target.value);
                          if (errors.diastolic) {
                            setErrors((prev) => ({ ...prev, diastolic: "" }));
                          }
                        }}
                      />
                      {errors.diastolic && (
                        <div className="invalid-feedback">
                          {errors.diastolic}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Assign To field */}
                <div className="form-group mb-3 mb-sm-4 p-3 border rounded-8 shadow-sm bg-white">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Assign To
                  </label>
                  <AsyncSelect
                    isClearable
                    name="assignTo"
                    loadOptions={loadAssineeOptions}
                    defaultOptions={false}
                    cacheOptions={false}
                    placeholder={t("Select Assignee")}
                    styles={getCustomStyles()}
                    value={assignTo}
                    onChange={setAssignTo}
                  />

                  {assignTo ? <PatientDetailsCard patient={assignTo} /> : null}
                </div>
              </div>

              {/* Blood Pressure Interpretation Info */}
              <div className="p-4 border rounded-10 shadow-sm bg-gradient-light position-relative overflow-hidden">
                {/* <div className="position-absolute top-0 end-0 p-2 opacity-50">
                <span className="material-symbols-outlined text-primary fs-1">
                  info
                </span>
              </div> */}
                <div className="row align-items-center mb-3">
                  <div className="col">
                    <h5 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                      <span className="material-symbols-outlined text-primary">
                        assessment
                      </span>
                      Blood Pressure Interpretation
                    </h5>
                    <p className="text-muted fs-sm mb-0">
                      Reference ranges for adults
                    </p>
                  </div>
                </div>

                <div className="interpretation-grid">
                  <div className="interpretation-item mb-3 p-3 bg-white rounded-8 border-start border-4 border-primary">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <div
                        className="status-dot bg-primary rounded-circle"
                        style={{ width: "8px", height: "8px" }}
                      ></div>
                      <span className="fw-semibold text-primary fs-sm">
                        Normal
                      </span>
                    </div>
                    <div className="d-flex flex-wrap gap-3 text-dark fs-sm">
                      <span>
                        <strong>Systolic:</strong> 90-120 mmHg
                      </span>
                      <span>
                        <strong>Diastolic:</strong> 60-80 mmHg
                      </span>
                    </div>
                  </div>

                  <div className="interpretation-item mb-3 p-3 bg-white rounded-8 border-start border-4 border-primary">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <div
                        className="status-dot bg-primary rounded-circle"
                        style={{ width: "8px", height: "8px" }}
                      ></div>
                      <span className="fw-semibold text-primary fs-sm">
                        Elevated
                      </span>
                    </div>
                    <div className="d-flex flex-wrap gap-3 text-dark fs-sm">
                      <span>
                        <strong>Systolic:</strong> 120-129 mmHg
                      </span>
                      <span>
                        <strong>Diastolic:</strong> 80 mmHg
                      </span>
                    </div>
                  </div>

                  <div className="interpretation-item mb-3 p-3 bg-white rounded-8 border-start border-4 border-primary">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <div
                        className="status-dot bg-primary rounded-circle"
                        style={{ width: "8px", height: "8px" }}
                      ></div>
                      <span className="fw-semibold text-primary fs-sm">
                        High Stage 1
                      </span>
                    </div>
                    <div className="d-flex flex-wrap gap-3 text-dark fs-sm">
                      <span>
                        <strong>Systolic:</strong> 130-139 mmHg
                      </span>
                      <span>
                        <strong>Diastolic:</strong> 80-89 mmHg
                      </span>
                    </div>
                  </div>

                  <div className="interpretation-item p-3 bg-white rounded-8 border-start border-4 border-primary">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <div
                        className="status-dot bg-primary rounded-circle"
                        style={{ width: "8px", height: "8px" }}
                      ></div>
                      <span className="fw-semibold text-primary fs-sm">
                        High Stage 2
                      </span>
                    </div>
                    <div className="d-flex flex-wrap gap-3 text-dark fs-sm">
                      <span>
                        <strong>Systolic:</strong> ≥140 mmHg
                      </span>
                      <span>
                        <strong>Diastolic:</strong> ≥90 mmHg
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-top border-gray-200">
                  <p className="text-muted fs-xs mb-0 d-flex align-items-center gap-1">
                    <span className="material-symbols-outlined fs-6">info</span>
                    Consult your healthcare provider for proper interpretation
                    and treatment recommendations.
                  </p>
                </div>
              </div>

              <div className="action d-flex justify-content-end pt-3 border-top mt-auto">
                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center gap-2"
                  onClick={saveBloodPressureHandler}
                  style={{ cursor: isSaving ? "not-allowed" : "pointer" }}
                  disabled={isSaving ? true : false}
                >
                  {isSaving ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      {t("Saving...")}
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined me-1">
                        save
                      </span>{" "}
                      {t("Save Blood Pressure")}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default SaveBloodPressurePopup;
